import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import * as S from './styles';

type TextDisclaimer = {
  key: string;
  icon: React.ReactNode;
  textKey: string;
};

type LinkDisclaimer = TextDisclaimer & {
  url: string;
  urlTextKey: string;
  urlMarker: string;
};

type DisclaimerLinkProps = {
  i18nKey: string;
  url: string;
};

type Disclaimer = TextDisclaimer | LinkDisclaimer;

const DISCLAIMERS: Disclaimer[] = [
  {
    key: 'intended_purpose',
    icon: <S.IntendedPurposeIcon />,
    textKey: 'modals.about_nova.sections.intended_purpose',
  },
  {
    key: 'ai_generated',
    icon: <S.AIGeneratedIcon />,
    textKey: 'modals.about_nova.sections.ai_generated',
  },
  {
    key: 'medical_disclaimer',
    icon: <S.MedicalDisclaimerIcon />,
    textKey: 'modals.about_nova.sections.medical_disclaimer.text',
    url: '/help?referrer=nova',
    urlTextKey: 'modals.about_nova.sections.medical_disclaimer.link',
    urlMarker: 'disclaimer_link',
  },
  {
    key: 'company_info',
    icon: <S.OfficeIcon />,
    textKey: 'modals.about_nova.sections.company_info',
  },
  {
    key: 'confidentiality',
    icon: <S.ConfidentialityIcon />,
    textKey: 'modals.about_nova.sections.confidentiality',
  },
];

const TEXT_DISCLAIMERS: Disclaimer[] = [
  ...DISCLAIMERS,
  {
    key: 'anonymity',
    icon: <S.AnonymityIcon />,
    textKey: 'modals.about_nova.sections.anonymity',
  },
];

const VOICE_DISCLAIMERS: Disclaimer[] = [
  ...DISCLAIMERS,
  {
    key: 'anonymity',
    icon: <S.AnonymityIcon />,
    textKey: 'voice.disclaimer.sections.anonymity',
  },
  {
    key: 'headphones',
    icon: <S.HeadphonesIcon />,
    textKey: 'voice.disclaimer.sections.headphones',
  },
];

const isLinkDisclaimer = (
  disclaimer: TextDisclaimer | LinkDisclaimer,
): disclaimer is LinkDisclaimer => 'url' in disclaimer;

const DisclaimerLink = ({ i18nKey, url }: DisclaimerLinkProps) => {
  const { t: translate } = useTranslation('chatbot');

  return (
    <S.DisclaimerLink href={url}>
      {translate(i18nKey as never)}
    </S.DisclaimerLink>
  );
};

type AboutNovaProps = {
  inModal?: boolean;
  inVoiceDisclaimer?: boolean;
};

export const AboutNova = ({
  inModal = false,
  inVoiceDisclaimer = false,
}: AboutNovaProps) => {
  const { t: translate } = useTranslation('chatbot');
  const disclaimers = inVoiceDisclaimer ? VOICE_DISCLAIMERS : TEXT_DISCLAIMERS;

  return (
    <S.DisclaimerContainerWrapper $inModal={inModal}>
      {disclaimers.map((disclaimer, index) => (
        <S.DisclaimerContainer
          key={disclaimer.key}
          $isFirst={index === 0}
          $isLast={index === disclaimers.length - 1}
        >
          <S.DisclaimerIconContainer aria-hidden={true}>
            {disclaimer.icon}
          </S.DisclaimerIconContainer>
          <S.DisclaimerText>
            <Trans
              t={translate}
              i18nKey={disclaimer.textKey as never}
              components={
                isLinkDisclaimer(disclaimer)
                  ? {
                      [disclaimer.urlMarker]: (
                        <DisclaimerLink
                          i18nKey={disclaimer.urlTextKey}
                          url={disclaimer.url}
                        />
                      ),
                    }
                  : {}
              }
            />
          </S.DisclaimerText>
        </S.DisclaimerContainer>
      ))}
    </S.DisclaimerContainerWrapper>
  );
};
